import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    View,
    Text,
    ScrollView,
    StyleSheet,
    Pressable,
    ActivityIndicator,
} from "react-native";
import { format } from "date-fns";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button, Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { UpGradationService } from "../../../services/UpGradationService";

/* ================= THEME ================= */

const theme = {
    primary: "#F97316",
    primarySoft: "rgba(249,115,22,0.12)",
    primaryBorder: "rgba(249,115,22,0.35)",
    textDark: "#4A2311",
    textMedium: "rgba(26,71,49,0.65)",
    cardBg: "rgba(255,255,255,0.88)",
    borderLight: "rgba(0,0,0,0.06)",
    chipBg: "rgba(255,247,237,0.9)",
};

export function UpGradationScreen() {
    const navigation = useNavigation<any>();
    const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

    const [date, setDate] = useState(todayStr);
    const [entryId, setEntryId] = useState<string | null>(null);

    const [oneSeating, setOneSeating] = useState("");
    const [withoutOffence, setWithoutOffence] = useState("");
    const [dayStart, setDayStart] = useState("");
    const [prayers, setPrayers] = useState("");
    const [sravan, setSravan] = useState("");
    const [comments, setComments] = useState("");

    const [marks, setMarks] = useState(0);
    const [totalMarks, setTotalMarks] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isEditable, setIsEditable] = useState(true);

    useFocusEffect(
        useCallback(() => {
            loadEntry();
        }, [date])
    );

    const loadEntry = async () => {
        const res = await UpGradationService.getAgainstDate(date);

        if (res?.status === 200 && res.data) {
            const d = res.data;
            setEntryId(d.upGradationId);
            setOneSeating(String(d.oneSeating || ""));
            setWithoutOffence(String(d.withoutOffence || ""));
            setDayStart(String(d.dayStart || ""));
            setPrayers(String(d.inJapaPrayers || ""));
            setSravan(String(d.japaSravan || ""));
            setComments(d.comments || "");
            setMarks(d.marks || 0);
            setTotalMarks(d.totalMarks || 0);
            setIsEditable(d.isEditable);

        }
        else if (res.data === null) {
            setEntryId(null);
        }
        else {
            Flash.error(res.message);
        }
    };

    /* ===== CALENDAR ===== */

    const openCalendar = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            display: "calendar",
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    const newDate = format(selectedDate, "yyyy-MM-dd");
                    setDate(newDate);

                    loadEntry();
                    // reset fields while loading new date data
                    //   setEntryId(null);
                    //   setOneSeating("");
                    //   setWithoutOffence("");
                    //   setDayStart("");
                    //   setPrayers("");
                    //   setSravan("");
                    //   setComments("");
                    //   setMarks(0);
                }
            },
        });
    };

    /* calculate marks */

    useEffect(() => {
        const m =
            (parseInt(oneSeating) || 0) +
            (parseInt(withoutOffence) || 0) +
            (parseInt(dayStart) || 0) +
            (parseInt(prayers) || 0) +
            (parseInt(sravan) || 0);

        setMarks(m);
    }, [oneSeating, withoutOffence, dayStart, prayers, sravan]);

    /* SAVE */

    const onSave = async () => {
        try {
            setLoading(true);

            const payload = {
                upGradationId: entryId,
                date,
                oneSeating: +oneSeating || 0,
                withoutOffence: +withoutOffence || 0,
                dayStart: +dayStart || 0,
                inJapaPrayers: +prayers || 0,
                japaSravan: +sravan || 0,
                comments,
                marks,
            };

            
            const res = entryId
                ? await UpGradationService.update(payload)
                : await UpGradationService.create(payload);

            if (res?.status !== 200) {
                Flash.error("Save failed");
                return;
            }

            Flash.success("Success!", "Upgradation Saved Successfully!");
            navigation.navigate("UpGradationHistory");

        } catch {
            Flash.error("Save failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DivineBackground overlayOpacity={0.92}>
            <View style={{ flex: 1 }}>
                <Header title="Up-Gradation" showBack showProfile />

                <ScrollView contentContainerStyle={styles.container}>
                    {/* SUMMARY */}
                    <View style={styles.summaryCard}>
                        <Text style={styles.summaryTitle}>This Month's Progress</Text>
                        <View style={{ alignItems: "flex-end" }}>
                            <Text style={{ fontSize: 10, color: theme.textMedium }}>
                                TOTAL JAPA
                            </Text>
                            <Text style={styles.summaryValue}>{totalMarks}</Text>
                        </View>
                    </View>

                    {/* FORM */}
                    <View style={styles.card}>
                        <View style={styles.topRow}>

                            {/* DATE CHIP (NOW CLICKABLE) */}
                            <Pressable style={styles.dateChip} onPress={openCalendar}>
                                <Text style={{ marginRight: 6 }}>📅</Text>
                                <Text style={styles.dateChipText}>
                                    {format(new Date(date), "dd-MM-yyyy")}
                                </Text>
                            </Pressable>

                            <Pressable
                                style={styles.historyBtn}
                                onPress={() => navigation.navigate("UpGradationHistory")}
                            >
                                <Text style={styles.historyIcon}>🕘</Text>
                                <Text style={styles.historyText}>History</Text>
                            </Pressable>
                        </View>

                        <Input label="One Seating Japa" value={oneSeating} keyboardType="numeric" onChangeText={setOneSeating} />
                        <Input label="Without Offence Japa" value={withoutOffence} keyboardType="numeric" onChangeText={setWithoutOffence} />
                        <Input label="Day Start Japa" value={dayStart} keyboardType="numeric" onChangeText={setDayStart} />
                        <Input label="In Japa Prayers" value={prayers} keyboardType="numeric" onChangeText={setPrayers} />
                        <Input label="Japa Sravan" value={sravan} keyboardType="numeric" onChangeText={setSravan} />
                        <Input label="Comments" value={comments} onChangeText={setComments} />

                        {/* <View style={styles.calcBox}>
                            <Text style={{ color: theme.textMedium }}>Calculated Marks:</Text>
                            <Text style={styles.calcValue}>{marks}</Text>
                        </View> */}

                        {isEditable && (
                            <View style={styles.btnRow}>
                                <Pressable onPress={() => navigation.goBack()}
                                    style={styles.cancelBtn}>
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>

                                <View style={{ flex: 1 }}>
                                    <Button title={loading ? "Saving..." : "Save"} onPress={onSave} />
                                </View>
                            </View>
                        )}

                        {loading && <ActivityIndicator style={{ marginTop: 12 }} />}
                    </View>
                </ScrollView>
            </View>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    container: { padding: 14, gap: 14 },

    summaryCard: {
        borderRadius: 16,
        padding: 16,
        backgroundColor: theme.cardBg,
        borderWidth: 1,
        borderColor: theme.borderLight,
        flexDirection: "row",
        justifyContent: "space-between",
    },

    summaryTitle: { fontWeight: "800", color: theme.textDark, textAlignVertical: "center" },

    summaryValue: {
        fontSize: 24,
        fontWeight: "900",
        color: theme.primary,
    },

    card: {
        borderRadius: 16,
        padding: 14,
        backgroundColor: theme.cardBg,
        borderWidth: 1,
        borderColor: theme.borderLight,
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 8 },
        elevation: 4,
    },

    topRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    dateChip: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.chipBg,
        borderWidth: 1,
        borderColor: theme.primaryBorder,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
    },

    dateChipText: {
        fontSize: 13,
        fontWeight: "800",
        color: theme.primary,
    },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        height: 34,
        borderRadius: 10,
        borderWidth: 1,
        paddingHorizontal: 10,
        borderColor: theme.borderLight,
    },

    historyIcon: { fontSize: 14 },

    historyText: {
        fontSize: 12,
        fontWeight: "800",
        color: theme.textMedium,
    },

    calcBox: {
        flexDirection: "row",
        justifyContent: "space-between",
        padding: 12,
        borderRadius: 12,
        backgroundColor: theme.primarySoft,
        borderWidth: 1,
        borderColor: theme.primaryBorder,
        marginTop: 6,
    },

    calcValue: {
        fontSize: 18,
        fontWeight: "900",
        color: theme.primary,
    },

    btnRow: {
        flexDirection: "row",
        gap: 10,
        marginTop: 18,
    },

    cancelBtn: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.borderLight,
        backgroundColor: "rgba(255,255,255,0.9)",
        alignItems: "center",
        justifyContent: "center",
    },

    cancelText: {
        fontSize: 14,
        fontWeight: "900",
        color: theme.textMedium,
    },
});